import type { ChainId, TokenSymbol } from '@cryptopay/core';
import type { AuthSession } from '../lib/auth.js';
import type { RelayTransferResponse } from '../lib/api.js';

export type SessionStep =
  | 'anonymous'
  | 'authenticated'
  | 'on-ramp-opened'
  | 'transfer-pending'
  | 'transfer-confirmed'
  | 'error';

export interface SessionState {
  readonly step: SessionStep;
  readonly auth: AuthSession | null;
  readonly chainId: ChainId;
  readonly tokenSymbol: TokenSymbol;
  readonly balance: bigint;
  readonly lastTransfer: RelayTransferResponse | null;
  readonly errorMessage: string | null;
}

export const initialSessionState = (
  chainId: ChainId,
  tokenSymbol: TokenSymbol,
): SessionState => ({
  step: 'anonymous',
  auth: null,
  chainId,
  tokenSymbol,
  balance: 0n,
  lastTransfer: null,
  errorMessage: null,
});

export type SessionAction =
  | { type: 'login-success'; auth: AuthSession }
  | { type: 'logout' }
  | { type: 'balance-loaded'; balance: bigint }
  | { type: 'on-ramp-opened' }
  | { type: 'transfer-pending' }
  | { type: 'transfer-confirmed'; transfer: RelayTransferResponse }
  | { type: 'error'; message: string }
  | { type: 'clear-error' };

export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case 'login-success':
      return { ...state, step: 'authenticated', auth: action.auth, errorMessage: null };
    case 'logout':
      return initialSessionState(state.chainId, state.tokenSymbol);
    case 'balance-loaded':
      return { ...state, balance: action.balance };
    case 'on-ramp-opened':
      return { ...state, step: 'on-ramp-opened', errorMessage: null };
    case 'transfer-pending':
      return { ...state, step: 'transfer-pending', errorMessage: null };
    case 'transfer-confirmed':
      return {
        ...state,
        step: 'transfer-confirmed',
        lastTransfer: action.transfer,
        errorMessage: null,
      };
    case 'error':
      return { ...state, step: 'error', errorMessage: action.message };
    case 'clear-error':
      return { ...state, step: state.auth ? 'authenticated' : 'anonymous', errorMessage: null };
  }
}
